import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Globe, ShieldAlert, Sparkles, ExternalLink, ShieldCheck } from 'lucide-react';
import { presentationHubAPI } from '@/shared/services/api';

export default function PublicPortalPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalData, setPortalData] = useState(null);

  const handleDeckOpen = (deck) => {
    presentationHubAPI.trackPublicPortalEvent(slug, {
      action: 'deck_open',
      deckId: deck.id,
      publicPath: deck.publicPath,
    }).catch(() => {});
  };

  useEffect(() => {
    document.body.classList.add('presentation-portal-active');
    
    presentationHubAPI.getPublicPortal(slug)
      .then((data) => {
        setPortalData(data);
        setError('');
      })
      .catch((err) => {
        setError(err.message || 'The requested portal could not be found or is unavailable.');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      document.body.classList.remove('presentation-portal-active');
    };
  }, [slug]);

  if (loading) {
    return (
      <main className="portal-container loading">
        <style>{styles}</style>
        <div className="spinner"></div>
      </main>
    );
  }

  if (error || !portalData) {
    return (
      <main className="portal-container error">
        <style>{styles}</style>
        <div className="error-card">
          <ShieldAlert className="error-icon" />
          <h1>Portal Access Restrained</h1>
          <p>{error || 'This link may have expired or is incorrect.'}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="portal-container">
      <style>{styles}</style>
      
      <header className="portal-header animate-fade-in">
        <div className="badge">
          <Sparkles className="badge-icon" />
          <span>Curated Presentation Suite</span>
        </div>
        <h1>Welcome, {portalData.recipient}</h1>
        <p>Explore your personalized selection of platform presentations and executive decks.</p>
      </header>

      <section className="privacy-notice animate-fade-in" aria-label="Portal access activity notice">
        <ShieldCheck className="privacy-icon" />
        <div>
          <h2>{portalData.privacyNotice?.title || 'Access activity is recorded'}</h2>
          <p>
            {portalData.privacyNotice?.body ||
              'For security and audit purposes, this portal records access activity such as IP address, date/time, browser/device information, and presentation interactions.'}
          </p>
        </div>
      </section>

      <section className="portal-grid animate-slide-up">
        {portalData.decks && portalData.decks.length > 0 ? (
          portalData.decks.map((deck) => (
            <div key={deck.id} className="portal-card">
              <div className="portal-card-glow"></div>
              <div className="portal-card-content">
                <h2 className="deck-title">{deck.name}</h2>
                <p className="deck-desc">{deck.description}</p>
                <div className="action-wrapper">
                  <a 
                    href={deck.publicPath} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="portal-btn"
                    onClick={() => handleDeckOpen(deck)}
                  >
                    <span>{deck.buttonText}</span>
                    <ExternalLink className="btn-icon" />
                  </a>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <Globe className="empty-icon" />
            <p>No presentations have been assigned to this portal yet.</p>
          </div>
        )}
      </section>

      <footer className="portal-footer">
        <p>Powered by EzERP CRM Presentation Hub &bull; Secured Local-First Node</p>
      </footer>
    </main>
  );
}

// Embedded High-Fidelity Premium Dark CSS Styles
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Prata&family=Space+Grotesk:wght@300;400;500;600&display=swap');

body.presentation-portal-active {
  background-color: #050507 !important;
  margin: 0;
  padding: 0;
  min-height: 100vh;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #f3f4f6;
  -webkit-font-smoothing: antialiased;
}

.portal-container {
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 4rem 2rem 2rem 2rem;
  background: radial-gradient(circle at top, rgba(16, 16, 24, 0.6) 0%, rgba(5, 5, 7, 1) 70%);
}

.portal-container.loading {
  justify-content: center;
  align-items: center;
}

.portal-container.error {
  justify-content: center;
  align-items: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-card {
  text-align: center;
  max-width: 480px;
  padding: 3rem 2rem;
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
}

.error-icon {
  width: 48px;
  height: 48px;
  color: #ef4444;
  margin-bottom: 1.5rem;
}

.error-card h1 {
  font-family: 'Prata', serif;
  font-size: 2rem;
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-weight: 400;
  letter-spacing: -0.02em;
}

.error-card p {
  color: #9ca3af;
  font-size: 0.975rem;
  line-height: 1.6;
  margin: 0;
}

.portal-header {
  text-align: center;
  max-width: 680px;
  margin-bottom: 4rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
}

.badge-icon {
  width: 14px;
  height: 14px;
  color: #ffffff;
}

.badge span {
  font-size: 0.775rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #e5e7eb;
}

.portal-header h1 {
  font-family: 'Prata', serif;
  font-size: 3rem;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: 400;
  letter-spacing: -0.03em;
  color: #ffffff;
}

.portal-header p {
  color: #9ca3af;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
}

.privacy-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  width: 100%;
  max-width: 820px;
  box-sizing: border-box;
  margin: -2.5rem 0 3rem 0;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d1d5db;
}

.privacy-icon {
  width: 20px;
  height: 20px;
  color: #34d399;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.privacy-notice h2 {
  margin: 0 0 0.25rem 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #f9fafb;
  letter-spacing: 0;
}

.privacy-notice p {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #9ca3af;
}

.portal-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;
  max-width: 1100px;
  margin-bottom: 4rem;
}

@media (max-width: 1024px) {
  .portal-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .portal-grid {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
  .portal-header h1 {
    font-size: 2.25rem;
  }
}

.portal-card {
  position: relative;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

.portal-card:hover {
  transform: translateY(-5px);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  background: rgba(255, 255, 255, 0.03);
}

.portal-card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: radial-gradient(circle 80px at 50% 0%, rgba(255, 255, 255, 0.05), transparent);
  pointer-events: none;
  transition: opacity 0.4s ease;
}

.portal-card:hover .portal-card-glow {
  background: radial-gradient(circle 100px at 50% 0%, rgba(255, 255, 255, 0.09), transparent);
}

.portal-card-content {
  padding: 2.25rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  flex-grow: 1;
  box-sizing: border-box;
}

.deck-title {
  font-family: 'Prata', serif;
  font-size: 1.85rem;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1rem;
  color: #ffffff;
  letter-spacing: -0.01em;
  line-height: 1.25;
  text-align: center;
}

.deck-desc {
  font-size: 0.9rem;
  color: #9ca3af;
  line-height: 1.6;
  text-align: center;
  margin-top: 0;
  margin-bottom: 2.25rem;
  flex-grow: 1;
  font-weight: 300;
}

.action-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.portal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 9999px;
  padding: 0.65rem 1.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  background: transparent;
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;
  cursor: pointer;
}

.portal-btn:hover {
  background: #ffffff;
  color: #050507;
  border-color: #ffffff;
}

.btn-icon {
  width: 14px;
  height: 14px;
  transition: transform 0.3s ease;
}

.portal-btn:hover .btn-icon {
  transform: translate(1px, -1px);
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.portal-footer {
  text-align: center;
  padding-top: 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
  width: 100%;
  max-width: 1100px;
}

.portal-footer p {
  color: #4b5563;
  font-size: 0.775rem;
  letter-spacing: 0.02em;
  margin: 0;
}

.animate-fade-in {
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-slide-up {
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
`;
